import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import css from './SectionHero.module.css';
import { CountUp, NamedLink } from '../../../components';
import { t } from '../../../util/messages';
import config from '../../../config/config';

/**
 * Page hero.
 *
 * Three moving parts:
 *  - a type-led left column with two trust badges;
 *  - a layered product "stage" built from real project imagery;
 *  - a logo marquee, then a stats deck that straddles the section boundary
 *    into the band below (see .deck).
 */

/**
 * Headline figures.
 *
 * The "120+ experts" claim was removed: it came from the original copy but
 * is not a number Gitribe can substantiate. Every figure here must be one
 * we can stand behind.
 */

const HERO_STATS = [
  { id: 'years', value: 'SectionStatsAndProcess.yearsCount', label: 'SectionStatsAndProcess.years' },
  { id: 'brands', value: 'SectionHero.brandsCount', label: 'SectionHero.brands' },
  {
    id: 'satisfaction',
    value: 'SectionStatsAndProcess.clientSatisfactionPercentage',
    label: 'SectionStatsAndProcess.clientSatisfaction',
  },
];

/**
 * Per-project crop overrides for the stage.
 *
 * The hero frames are a different shape from the portfolio cards, so a shot
 * can need a different crop in each place — hence `heroFraming` rather than
 * reusing `imagePosition` / `imageScale`. Both are optional; the slot
 * defaults in SectionHero.module.css apply when a project sets neither.
 */
const framing = project => {
  const { position, scale } = project.heroFraming || {};
  return {
    '--shot-position': position || undefined,
    '--shot-scale': scale || undefined,
  };
};

const ArrowIcon = () => (
  <svg
    className={css.arrow}
    width="15"
    height="11"
    viewBox="0 0 15 11"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M9.5 1L14 5.5 9.5 10M14 5.5H0"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SectionHero = ({ rootClassName, className }) => {
  const classes = classNames(rootClassName || css.root, className);
  const { featuredClients, portfolioConfig } = config;
  const projects = portfolioConfig.configPortfolio;

  const primaryShot = projects.find(p => p.id === 'dialux') || projects[0];
  const secondaryShot = projects.find(p => p.id === 'thecarelist') || projects[1];

  return (
    <section className={classes} aria-label="Hero">
      {/*
        The aura is clipped by its own wrapper rather than by the section.
        The section must NOT clip: the stats deck deliberately overflows its
        bottom edge into the band below.
      */}
      <div className={css.auraClip} aria-hidden="true">
        <div className={css.aura} />
      </div>

      <div className={css.content}>
        <div className={css.grid}>
          <div className={css.copy}>
            <h1 className={css.title} data-reveal>
              {t('SectionHero.title1')}{' '}
              <br />
              <span className={css.lit}>{t('SectionHero.title2')}</span>
            </h1>

            <p className={css.intro} data-reveal data-reveal-delay="70">
              {t('SectionHero.intro')}
            </p>

            <div className={css.actions} data-reveal data-reveal-delay="140">
              <NamedLink
                className={css.primaryAction}
                name="LandingPage"
                title={t('Topbar.startProject')}
                to={{ hash: '#contact-us' }}
              >
                {t('Topbar.startProject')}
                <ArrowIcon />
              </NamedLink>
              <NamedLink
                className={css.secondaryAction}
                name="LandingPage"
                title={t('SectionHero.exploreWork')}
                to={{ hash: '#portfolio' }}
              >
                {t('SectionHero.exploreWork')}
              </NamedLink>
            </div>
          </div>

          <div className={css.stage} data-reveal data-reveal-delay="140">
            <div className={css.stageGlow} aria-hidden="true" />
            <figure className={classNames(css.shot, css.shotPrimary)}>
              <img
                src={primaryShot.image}
                alt={primaryShot.title}
                width="900"
                height="800"
                style={framing(primaryShot)}
              />
            </figure>
            <figure className={classNames(css.shot, css.shotSecondary)}>
              <img
                src={secondaryShot.image}
                alt={secondaryShot.title}
                width="600"
                height="600"
                style={framing(secondaryShot)}
              />
            </figure>
          </div>
        </div>

        <div className={css.trust} data-reveal>
          <p className={css.trustLabel}>{t('SectionHero.featuredClients')}</p>
          <div className={css.marquee}>
            {/* Duplicated once so the track can loop seamlessly at -50%. */}
            <div className={css.marqueeTrack}>
              {[...featuredClients, ...featuredClients].map((client, index) => (
                <img
                  key={`${client.id}-${index}`}
                  className={css.clientLogo}
                  src={client.image}
                  alt={index < featuredClients.length ? client.alt : ''}
                  aria-hidden={index >= featuredClients.length}
                  width="120"
                  height="40"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Straddles the boundary into the section below. */}
      <div className={css.deck} data-reveal>
        <dl className={css.deckInner}>
          {HERO_STATS.map(stat => (
            <div key={stat.id} className={css.stat}>
              <dt className={css.statValue}>
                <CountUp value={t(stat.value)} />
              </dt>
              <dd className={css.statLabel}>{t(stat.label)}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

SectionHero.propTypes = {
  rootClassName: string,
  className: string,
};

export default React.memo(SectionHero);
